import * as BABYLON from "@babylonjs/core/Legacy/legacy";

import { config } from "../config.js";
import { GameScene } from "../scene.js";
import { IMeshPool } from "./common.js";

const segmentSize = config.pathSegmentSize;
const log = console.log.bind(console);

export enum BasicPathType {
	straight,
	turnLeft,
	turnRight,
}

export class BasicPathMeshPool implements IMeshPool {
	meshes: BABYLON.Mesh[];
	scene: GameScene;
	type: BasicPathType;

	constructor(scene: GameScene, type: BasicPathType) {
		this.meshes = [];
		this.scene = scene;
		this.type = type;
		this.createMeshes();
	}

	createMeshes() {
		// 我们用两块地面来回交替 模拟无限的地面
		let floorSize = segmentSize;

		let bbScene = this.scene.bbScene;
		var floor = new BABYLON.StandardMaterial("floor_basic", bbScene);
		floor.diffuseTexture = this.scene
			.GetAsset()
			.loadTexture("./assets/grassy_cobblestone_diff.jpg");
		floor.bumpTexture = this.scene
			.GetAsset()
			.loadTexture("./assets/grassy_cobblestone_nor_gl.png");
		floor.specularColor = new BABYLON.Color3(0, 0, 0);

		let matWall = new BABYLON.StandardMaterial("wall_basic", bbScene);
		matWall.diffuseTexture = this.scene
			.GetAsset()
			.loadTexture("./assets/dark_brick_wall_diff.jpg");
		matWall.specularColor = new BABYLON.Color3(0, 0, 0);

		const createStraightMesh = function (i: number): BABYLON.Mesh {
			// 主路面
			let box1 = BABYLON.MeshBuilder.CreateBox(`box_${i}`, {});
			box1.scaling.x = floorSize;
			box1.scaling.z = floorSize;
			box1.scaling.y = config.pathBoxThick;
			box1.material = floor;
			box1.checkCollisions = true;

			// 路面左侧墙
			let box2 = BABYLON.MeshBuilder.CreateBox(`box_${i}_zuo`, {});
			let h = config.pathBoxThick * 2;
			let w = floorSize / 16;
			box2.scaling.x = w;
			box2.scaling.z = floorSize;
			box2.scaling.y = h;
			box2.position.x -= floorSize / 2 + w / 2;
			box2.position.y += h / 2 - config.pathBoxThick / 2;

			box2.material = matWall;

			// 路面右侧墙
			let box3 = BABYLON.MeshBuilder.CreateBox(`box_${i}_you`, {});
			box3.scaling.x = w;
			box3.scaling.z = floorSize;
			box3.scaling.y = h;
			box3.position.x += floorSize / 2 + w / 2;
			box3.position.y += h / 2 - config.pathBoxThick / 2;

			box3.material = matWall;

			// 合并后的 mesh
			var mesh = BABYLON.Mesh.MergeMeshes(
				[box1, box2, box3],
				true,
				true,
				undefined,
				false,
				true,
			);
			mesh.position.y = -10;

			return mesh;
		};

		const createTurnLeftMesh = function (i: number): BABYLON.Mesh {
			// 主路面
			let box1 = BABYLON.MeshBuilder.CreateBox(`box_${i}`, {});
			box1.scaling.x = floorSize;
			box1.scaling.z = floorSize;
			box1.scaling.y = config.pathBoxThick;
			box1.material = floor;
			box1.checkCollisions = true;

			// 路面前侧墙
			let box2 = BABYLON.MeshBuilder.CreateBox(`box_${i}_zuo`, {});
			let h = config.pathBoxThick * 2;
			let w = floorSize / 16;

			box2.scaling.x = floorSize;
			box2.scaling.z = w;
			box2.scaling.y = h;

			box2.position.z += floorSize / 2 + w / 2;
			box2.position.y += h / 2 - config.pathBoxThick / 2;

			box2.material = matWall;

			// 路面右侧墙
			let box3 = BABYLON.MeshBuilder.CreateBox(`box_${i}_you`, {});

			box3.scaling.x = w;
			box3.scaling.z = floorSize;
			box3.scaling.y = h;

			box3.position.x += floorSize / 2 + w / 2;
			box3.position.y += h / 2 - config.pathBoxThick / 2;

			box3.material = matWall;

			// 合并后的 mesh
			var mesh = BABYLON.Mesh.MergeMeshes(
				[box1, box2, box3],
				true,
				true,
				undefined,
				false,
				true,
			);
			mesh.position.y = -10;

			return mesh;
		};

		const createTurnRightMesh = function (i: number): BABYLON.Mesh {
			// 主路面
			let box1 = BABYLON.MeshBuilder.CreateBox(`box_${i}`, {});
			box1.scaling.x = floorSize;
			box1.scaling.z = floorSize;
			box1.scaling.y = config.pathBoxThick;
			box1.material = floor;
			box1.checkCollisions = true;

			// 路面前侧墙
			let box2 = BABYLON.MeshBuilder.CreateBox(`box_${i}_qian`, {});
			let h = config.pathBoxThick * 2;
			let w = floorSize / 16;

			box2.scaling.x = floorSize;
			box2.scaling.z = w;
			box2.scaling.y = h;

			box2.position.z += floorSize / 2 + w / 2;
			box2.position.y += h / 2 - config.pathBoxThick / 2;

			box2.material = matWall;

			// 路面左侧墙
			let box3 = BABYLON.MeshBuilder.CreateBox(`box_${i}_zuo`, {});
			box3.scaling.x = w;
			box3.scaling.z = floorSize;
			box3.scaling.y = h;
			box3.position.x -= floorSize / 2 + w / 2;
			box3.position.y += h / 2 - config.pathBoxThick / 2;

			box3.material = matWall;

			// 合并后的 mesh
			var mesh = BABYLON.Mesh.MergeMeshes(
				[box1, box2, box3],
				true,
				true,
				undefined,
				false,
				true,
			);
			mesh.position.y = -10;

			return mesh;
		};

		let f = createStraightMesh;
		if (this.type === BasicPathType.turnLeft) {
			f = createTurnLeftMesh;
		} else if (this.type === BasicPathType.turnRight) {
			f = createTurnRightMesh;
		}

		for (let i = 0; i < config.pathSegmentPreloadNum; i++) {
			let box = f(i);
			this.meshes.push(box);
		}
		// log('createMeshes', this.meshes)
	}

	getMesh(): BABYLON.Mesh {
		let res = this.meshes.pop();
		res.position.y = config.groundHeight - config.pathBoxThick / 2;
		return res;
	}

	returnMesh(mesh: BABYLON.Mesh) {
		mesh.position.y = -10;
		this.meshes.push(mesh);
	}
}
